import React, { useState, useEffect } from 'react';
import {
  getUserNotifications, 
  getUnreadNotificationsCount, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  subscribeToUserNotifications
} from '../services/notificationService';
import { getUserApplications } from '../services/applicationService';
import { useNavigate } from 'react-router-dom';

const NotificationCenter = ({ user, isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.uid && isOpen) {
      fetchNotifications();
      fetchUnreadCount();
      
      // Set up real-time listener
      const unsubscribe = subscribeToUserNotifications(user.uid, (updatedNotifications) => {
        if (updatedNotifications.length > 0) {
          setNotifications(updatedNotifications);
        } else {
          loadApplicationNotificationFallback();
        }
        // Update unread count
        const unread = updatedNotifications.filter(n => n.status === 'unread').length;
        setUnreadCount(unread);
      });
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [user, isOpen]);

  const fetchNotifications = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      const result = await getUserNotifications(user.uid, 20);
      if (result.success) {
        if (result.notifications.length > 0) {
          setNotifications(result.notifications);
        } else {
          await loadApplicationNotificationFallback();
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplicationNotificationFallback = async () => {
    if (!user?.uid) return;

    try {
      const result = await getUserApplications(user.uid, { limit: 20 });
      if (!result.success || result.applications.length === 0) {
        setNotifications([]);
        return;
      }

      const applicationNotifications = result.applications.map((application) => {
        const jobTitle = application.jobTitle || 'this job';
        const companyName = application.companyName || 'the company';

        return {
          id: `application-submitted-${application.id}`,
          isVirtual: true,
          type: 'application_submitted',
          status: 'read',
          title: 'Application Submitted',
          message: `Job based on your profile: ${jobTitle} at ${companyName}. Your application has been submitted.`,
          createdAt: application.appliedAt || application.updatedAt || null,
          data: {
            applicationId: application.id,
            jobId: application.jobId,
            jobTitle: application.jobTitle,
            company: application.companyName,
            type: 'application_submitted'
          }
        };
      });

      setNotifications(applicationNotifications);
    } catch (error) {
      console.error('Error loading application notification fallback:', error);
      setNotifications([]);
    }
  };

  const fetchUnreadCount = async () => {
    if (!user?.uid) return;
    
    try {
      const result = await getUnreadNotificationsCount(user.uid);
      if (result.success) {
        setUnreadCount(result.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isVirtual && notification.status === 'unread') {
      await markNotificationAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'package_created' && notification.data?.packageId) {
      navigate('/packages');
      onClose();
    } else if (notification.type === 'new_job' && notification.data?.jobId) {
      navigate(`/jobs/${notification.data.jobId}`);
      onClose();
    } else if (notification.type === 'new_application') {
      navigate('/applications');
      onClose();
    } else if (notification.type === 'application_submitted') {
      navigate('/application-history');
      onClose();
    } else if (notification.type === 'application_status_update' && notification.data?.applicationId) {
      navigate('/application-history');
      onClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.uid) return;
    
    try {
      await markAllNotificationsAsRead(user.uid);
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    
    try {
      await deleteNotification(notificationId);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const notificationTime = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return notificationTime.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'package_created':
        return '📦';
      case 'package_updated':
        return '🔄';
      case 'subscription_reminder':
        return '⏰';
      case 'new_job':
        return '💼';
      case 'new_application':
      case 'application_submitted':
        return '📝';
      case 'application_status_update':
        return '✅';
      default:
        return '🔔';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-2">🔔</div>
              <h4 className="text-gray-900 font-medium mb-1">No notifications</h4>
              <p className="text-gray-500 text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    notification.status === 'unread' ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`text-sm font-medium ${
                            notification.status === 'unread' ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            notification.status === 'unread' ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification.message || notification.body}
                          </p>
                          {notification.data?.packagePrice && (
                            <div className="mt-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded inline-block">
                              ${notification.data.packagePrice} for {notification.data.packageDuration} days
                            </div>
                          )}
                        </div>
                        {!notification.isVirtual && (
                        <button
                          onClick={(e) => handleDeleteNotification(notification.id, e)}
                          className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                        >
                          🗑️
                        </button>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                        {notification.status === 'unread' && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-4 border-t border-gray-200 text-center">
            <button
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
